import { Directive, ElementRef, Input } from "@angular/core";
import { Deck, DeckCard } from "@apiserver-rest/ponder-data-client-base";
import { DeckBaseComponent } from "./deck-base.component";
import { DeckLayerComponent } from "./deck-layer.component";
import { DeckGroupComponent } from "./group/deck-group.component";
import { DeckRowComponent } from "./deck-row.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { getDeckCardName } from "../utils";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { debounceTime, Subscription } from "rxjs";
import { formatCurrency } from "@ponder-shared/utils/numberFormatter";
import { DeckCardEvent } from "@ponder-core/events/deck.events";


@Directive()
export class DeckMultiRowComponent {
    @Input({ required: true }) public isOwnDeck!: boolean;
    @Input({ required: true }) public deck!: Deck;
    @Input({ required: true }) public deckComponent!: DeckBaseComponent;
    @Input({ required: true }) public deckLayerComponent!: DeckLayerComponent;
    @Input({ required: true }) public deckGroupComponent!: DeckGroupComponent;
    @Input({ required: true }) public deckCards!: DeckCard[];

    public readonly expandSvgIcon: string = SvgIconMap.get(SvgIcon.Expand)!;
    public readonly collapseSvgIcon: string = SvgIconMap.get(SvgIcon.Collapse)!;
    
    protected showChildRows: boolean = false;
    protected totalCount: number = 0;
    protected collectedCount: number = 0;
    protected deckRowComponents: DeckRowComponent[] = [];
    protected areAllChildrenFoil: boolean = false;

    private itemVisibilitySub!: Subscription;
    private deckCardChangeSub!: Subscription;

    public constructor(public elementRef: ElementRef, public events: EventBroker) {
        
    }

    public ngOnInit(): void {
        this.deckGroupComponent.registerRowComponent(this);

        this.sortRows();
        this.recalculateTotals();

        this.itemVisibilitySub = this.events.ui.itemVisibilityRecalculatedEvent
            .pipe(debounceTime(100))
            .subscribe({
                next: () => {
                    this.onDeckContentsUpdated();
                }
            });

        this.deckCardChangeSub = this.events.deck.deckCardEvent
            .pipe(debounceTime(100))
            .subscribe({
                next: (evt: DeckCardEvent) => {
                    if (getDeckCardName(evt.deckCard) == this.getCardName()) {
                        this.onDeckContentsUpdated();
                    }
                }
            });
    }

    public ngOnDestroy(): void {
        this.itemVisibilitySub.unsubscribe();
        this.deckCardChangeSub.unsubscribe();
        this.deckGroupComponent.unregisterRowComponent(this);
    }

    private onDeckContentsUpdated() {
        this.sortRows();
        this.recalculateTotals();
    }

    public recalculateTotals(): void {
        this.totalCount = 0;
        this.collectedCount = 0;
        this.areAllChildrenFoil = true;
        
        this.deckCards?.forEach(c => {
            this.totalCount += c.count + c.specialCount;
    
            this.collectedCount += 
                (c.card.collected?.count ?? 0) + 
                (c.card.collected?.specialCount ?? 0)+ 
                (c.card.collected?.proxyCount ?? 0);

            if (this.areAllChildrenFoil && c.count) {
                this.areAllChildrenFoil = false;
            }
        });
    }

    public sortRows(): void {
        this.deckCards.sort((a: DeckCard, b: DeckCard) => {
            var indexA = this.deckComponent.filteredItems.getCurrentIndex(a.card);
            var indexB = this.deckComponent.filteredItems.getCurrentIndex(b.card);

            return (indexA - indexB);
        });
    }

    public onCardHover(deckCard: DeckCard): void {
        this.deckComponent.onCardHover(deckCard);
    }

    public registerRowComponent(deckRowComponent: DeckRowComponent): void {
        this.deckRowComponents.push(deckRowComponent);
    }

    public unregisterRowComponent(deckRowComponent: DeckRowComponent): void {
        this.removeRowComponent(deckRowComponent.deckCard);
    }

    public matches(deckCard: DeckCard | undefined): boolean {
        if (!deckCard || !this.deckCards.length) {
            return false;
        }
        
        return getDeckCardName(this.deckCards[0]) == getDeckCardName(deckCard);
    }

    protected getRowComponent(deckCard: DeckCard | undefined): DeckRowComponent | undefined {
        if (!deckCard) {
            return undefined;
        }

        for (let i = 0; i < this.deckRowComponents.length; ++i) {
            if (this.deckRowComponents[i].deckCard.deckCardId === deckCard.deckCardId) { 
                return this.deckRowComponents[i];
            }
        }

        return undefined;
    }

    protected removeRowComponent(deckCard: DeckCard | undefined): DeckRowComponent | undefined {
        if (!deckCard) {
            return undefined;
        }

        for (let i = 0; i < this.deckRowComponents.length; ++i) {
            if (this.deckRowComponents[i].deckCard.deckCardId === deckCard.deckCardId) { 
                return this.deckRowComponents.splice(i, 1)[0];
            }
        }

        return undefined;
    }

    protected doesCardExist(deckCard: DeckCard): boolean {
        for (let i = 0; i < this.deckCards.length; ++i) {
            if (this.deckCards[i].card.item.id == deckCard.card.item.id) {
                return true;
            }
        }

        return false;
    }

    public addCard(deckCard: DeckCard): void {
        if (!this.doesCardExist(deckCard)) {
            var preexistingRow = this.getRowComponent(deckCard);

            if (!preexistingRow) {
                this.deckCards.push(deckCard);
            }
        }

        this.sortRows();
        this.recalculateTotals();
    }

    public adjustCount(delta: number): void {
        if (!delta) {
            return;
        }

        this.totalCount += delta;
    }

    public getCollectedCount(): string {
        var count = Math.min(this.totalCount, this.collectedCount);
        return (count > 0 ? `${count} /` : "");
    }

    public getTotalCount(): number {
        return this.totalCount;
    }

    public getTotalValue(): string {
        var value = 0;

        this.deckCards.forEach(dc => {
            value += (dc.count * dc.card.price.normalUSD) + (dc.specialCount * dc.card.price.specialUSD);
        });

        if (value > 100) {
            return "$" + value.toFixed(0);
        } else {
            return formatCurrency(value);
        }
    }

    public getCardName(): string {
        return getDeckCardName(this.deckCards[0]);
    }

    public shouldShowChildren(): boolean {
        return this.showChildRows;
    }

    public onToggleChildrenClicked(): void {
        this.showChildRows = !this.showChildRows;
    }

    public areAnyCardsVisible(): boolean {
        for (let i = 0; i < this.deckCards.length; ++i) {
            if (this.deckComponent.isCardVisible(this.deckCards[i])) {
                return true;
            }
        }

        return false;
    }
}